// Unified analytics: Meta Pixel + Google Analytics 4 (gtag).
// Both are loaded dynamically from env-provided IDs and no-op gracefully when
// the IDs are absent, so nothing runs in preview/dev until configured.
const GA_ID = (process.env.REACT_APP_GA4_MEASUREMENT_ID || "").trim();
const PIXEL_ID = (process.env.REACT_APP_META_PIXEL_ID || "").trim();
let started = false;

export const hasAnalytics = () => Boolean(GA_ID || PIXEL_ID);

export function initAnalytics() {
  if (started || typeof window === "undefined" || typeof document === "undefined") return;
  started = true;

  // --- Google Analytics 4 (gtag.js) ---
  // The gtag snippet lives in public/index.html (loads on every page and sends
  // the initial page_view). This block is only a fallback if that snippet is absent.
  if (GA_ID && !window.gtag) {
    window.dataLayer = window.dataLayer || [];
    if (!window.gtag) {
      window.gtag = function () {
        window.dataLayer.push(arguments);
      };
    }
    window.gtag("js", new Date());
    // Disable automatic pageviews; the SPA tracker sends them manually.
    window.gtag("config", GA_ID, { send_page_view: false });
    if (!document.querySelector("script[data-ga4]")) {
      const s = document.createElement("script");
      s.async = true;
      s.src = `https://www.googletagmanager.com/gtag/js?id=${encodeURIComponent(GA_ID)}`;
      s.setAttribute("data-ga4", "true");
      document.head.appendChild(s);
    }
  }

  // --- Meta Pixel (fbevents.js) ---
  if (PIXEL_ID) {
    if (!window.fbq) {
      const n = (window.fbq = function () {
        n.callMethod ? n.callMethod.apply(n, arguments) : n.queue.push(arguments);
      });
      if (!window._fbq) window._fbq = n;
      n.push = n;
      n.loaded = true;
      n.version = "2.0";
      n.queue = [];
    }
    if (!document.querySelector("script[data-meta-pixel]")) {
      const s = document.createElement("script");
      s.async = true;
      s.src = "https://connect.facebook.net/en_US/fbevents.js";
      s.setAttribute("data-meta-pixel", "true");
      document.head.appendChild(s);
    }
    window.fbq("init", PIXEL_ID);
    // Initial PageView is sent by the SPA tracker (trackPageView) to avoid dupes.
  }
}

function gaEvent(name, params) {
  if (GA_ID && typeof window !== "undefined" && window.gtag) {
    window.gtag("event", name, params || {});
  }
}

function fbEvent(name, params, standard) {
  if (!PIXEL_ID || typeof window === "undefined" || typeof window.fbq !== "function") return;
  window.fbq(standard ? "track" : "trackCustom", name, params || {});
}

// Fire one semantic event to BOTH GA4 and Meta Pixel.
// metaStandard: optional Meta *standard* event name (e.g. "Lead", "Schedule",
// "Contact", "ViewContent"). When omitted, Meta receives a custom event.
export function track(name, params = {}, metaStandard = null) {
  gaEvent(name, params);
  if (metaStandard) fbEvent(metaStandard, params, true);
  else fbEvent(name, params, false);
}

// gtag('config') in index.html already sent a page_view for the URL the document
// loaded on; only send manual page_views for URLs after that (route changes).
let lastGaPageViewUrl = typeof window !== "undefined" ? window.location.href : "";

export function trackPageView() {
  if (typeof window === "undefined") return;
  if (window.location.href !== lastGaPageViewUrl) {
    lastGaPageViewUrl = window.location.href;
    gaEvent("page_view", {
      page_title: typeof document !== "undefined" ? document.title : "",
      page_location: window.location.href,
      page_path: window.location.pathname + window.location.search,
    });
  }
  // Meta PageView fires on every view, exactly as before.
  fbEvent("PageView", {}, true);
}

// Microsoft Clarity: tag the session with ad params so recordings can be
// filtered by ad. Safe before Clarity loads (the snippet queues calls).
const CLARITY_KEYS = ["utm_source", "utm_campaign", "utm_term", "utm_content"];
export function tagClaritySession(values) {
  if (typeof window === "undefined" || typeof window.clarity !== "function") return;
  CLARITY_KEYS.forEach((k) => {
    const v = values && values[k];
    if (v) window.clarity("set", k, String(v));
  });
}

// Returns onPlay/onTimeUpdate/onEnded handlers for an HTML5 <video>, emitting
// video_start, video_progress (25/50/75) and video_complete once each.
export function makeVideoTracker(meta = {}) {
  const fired = new Set();
  const info = (el) => ({
    video_title: meta.title || "video",
    video_provider: "html5",
    video_duration: Math.round(el && el.duration ? el.duration : 0),
    video_url: meta.id || "",
  });
  return {
    onPlay: (e) => {
      if (fired.has("start")) return;
      fired.add("start");
      track("video_start", { ...info(e.currentTarget), video_percent: 0 });
    },
    onTimeUpdate: (e) => {
      const el = e.currentTarget;
      if (!el || !el.duration) return;
      const pct = Math.floor((el.currentTime / el.duration) * 100);
      [25, 50, 75].forEach((t) => {
        const key = `p${t}`;
        if (pct >= t && !fired.has(key)) {
          fired.add(key);
          track("video_progress", { ...info(el), video_percent: t });
        }
      });
    },
    onEnded: (e) => {
      if (fired.has("complete")) return;
      fired.add("complete");
      track("video_complete", { ...info(e.currentTarget), video_percent: 100 });
    },
  };
}
